/**
 * useIntakeQueue — Composition hook
 *
 * Joins Won opportunities, IntakeRecords, and OpsGroups to produce
 * a unified intake queue with derived display statuses.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dataverseClient } from '@shared/api';
import { useWonOpportunities, useProductsSold } from '@entities/opportunity';
import type { Opportunity } from '@entities/opportunity';
import { useIntakeRecords } from '@entities/intake-record';
import type { IntakeRecord } from '@entities/intake-record';
import { useOpsGroups } from '@entities/ops-group';
import type { OpsGroup } from '@entities/ops-group';
import { soldIntoExistingGroup } from '../lib/upsellRule';
import { mecOnboardedIds } from '../lib/mecOnboarded';

/**
 * Won opportunities Vera's MEC onboarding created (ADO #1744), which the queue
 * leaves out: that workflow is their intake. A separate light read because the
 * shared opportunity select does not carry the description, and widening it
 * would add a memo column to every Sales and Ops opportunity query.
 */
function useMecOnboardedOppIds() {
  return useQuery({
    queryKey: ['opportunities', 'won', 'mecOnboarded'] as const,
    queryFn: async () =>
      mecOnboardedIds(
        await dataverseClient.listAll<{ opportunityid: string; description?: string | null }>('opportunities', {
          select: ['opportunityid', 'description'],
          filter: 'statecode eq 1',
        }),
      ),
    staleTime: 5 * 60 * 1000,
  });
}

export type DisplayStatus =
  | 'new'
  | 'in-review'
  | 'upsell'
  | 'pre-enrolled'
  | 'enrolled'
  | 'linked'
  | 'dismissed';

export interface IntakeQueueItem {
  opportunity: Opportunity;
  intakeRecord?: IntakeRecord;
  existingGroup?: OpsGroup;
  displayStatus: DisplayStatus;
  /**
   * What the rep actually sold, from the close form. Falls back to the
   * opportunity's Products to Quote when the close record has no sold data
   * yet, so the column is never blank for an older opp; `productsSource` says
   * which one you are looking at.
   */
  products: string[];
  productsSource: 'sold' | 'quoted';
}

export interface IntakeQueueResult {
  items: IntakeQueueItem[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  // Summary counts
  newCount: number;
  inReviewCount: number;
  upsellCount: number;
  preEnrolledCount: number;
  enrolledCount: number;
  linkedCount: number;
  dismissedCount: number;
}

export function useIntakeQueue(): IntakeQueueResult {
  const wonOpps = useWonOpportunities();
  const intakeRecords = useIntakeRecords();
  const opsGroups = useOpsGroups();
  const productsSold = useProductsSold();
  const mecOnboarded = useMecOnboardedOppIds();

  const items = useMemo(() => {
    if (!wonOpps.data) return [];
    const skip = mecOnboarded.data;

    // Index intake records by opportunityId for fast lookup
    const intakeByOppId = new Map<string, IntakeRecord>();
    for (const rec of intakeRecords.data ?? []) {
      if (rec.opportunityId) intakeByOppId.set(rec.opportunityId, rec);
    }

    // Index OpsGroups by accountId (Dynamics Account UUID)
    const groupByAccountId = new Map<string, OpsGroup>();
    for (const g of opsGroups.data ?? []) {
      if (g.accountId) groupByAccountId.set(g.accountId, g);
    }

    return wonOpps.data.filter((opp) => !skip?.has(opp.id)).map((opp): IntakeQueueItem => {
      const intakeRecord = intakeByOppId.get(opp.id);
      // opp.groupId (_dts_group_value) is a Dynamics Account UUID, match against OpsGroup.accountId
      const existingGroup = (opp.groupId ? groupByAccountId.get(opp.groupId) : undefined)
        ?? (opp.accountId ? groupByAccountId.get(opp.accountId) : undefined);

      const sold = productsSold.data?.get(opp.id);
      const hasSold = Boolean(sold && sold.length > 0);

      let displayStatus: DisplayStatus;
      if (soldIntoExistingGroup(opp, existingGroup, intakeRecord)) {
        displayStatus = 'upsell';
      } else if (existingGroup?.provisional) {
        // Pre-enrolled: the group shell exists so the admin fee breakout could
        // start, and the deal is still WORK - somebody has to enroll it for
        // real. Ahead of the `enrolled` branch on purpose, which would
        // otherwise read the group's existence as done and drop the row off
        // the queue (docs/design/pre-enrollment.md).
        displayStatus = 'pre-enrolled';
      } else if (existingGroup && (!intakeRecord || intakeRecord.status === 'in-review')) {
        displayStatus = 'enrolled';
      } else if (intakeRecord) {
        displayStatus = intakeRecord.status === 'in-review' ? 'in-review'
          : intakeRecord.status === 'linked' ? 'linked'
          : 'dismissed';
      } else {
        displayStatus = 'new';
      }

      return {
        opportunity: opp,
        intakeRecord,
        existingGroup,
        displayStatus,
        products: hasSold ? sold! : opp.productsToQuote,
        productsSource: hasSold ? 'sold' : 'quoted',
      };
    });
  }, [wonOpps.data, intakeRecords.data, opsGroups.data, productsSold.data, mecOnboarded.data]);

  const counts = useMemo(() => {
    const c = {
      newCount: 0, inReviewCount: 0, upsellCount: 0, preEnrolledCount: 0,
      enrolledCount: 0, linkedCount: 0, dismissedCount: 0,
    };
    for (const item of items) {
      if (item.displayStatus === 'new') c.newCount++;
      else if (item.displayStatus === 'in-review') c.inReviewCount++;
      else if (item.displayStatus === 'upsell') c.upsellCount++;
      else if (item.displayStatus === 'pre-enrolled') c.preEnrolledCount++;
      else if (item.displayStatus === 'enrolled') c.enrolledCount++;
      else if (item.displayStatus === 'linked') c.linkedCount++;
      else if (item.displayStatus === 'dismissed') c.dismissedCount++;
    }
    return c;
  }, [items]);

  return {
    items,
    // productsSold is part of the loading gate on purpose: rendering the
    // Products Sold column from the quoted fallback and then swapping it when
    // the close data lands is worse than waiting for it.
    // The MEC read gates loading too, so onboarded deals never flash in and
    // out. A failure of it is NOT an error: the queue then shows them, which
    // is the pre-#1744 behaviour, rather than blanking the page.
    isLoading: wonOpps.isLoading || intakeRecords.isLoading || opsGroups.isLoading
      || productsSold.isLoading || mecOnboarded.isLoading,
    isError: wonOpps.isError || intakeRecords.isError || opsGroups.isError,
    refetch: () => {
      wonOpps.refetch();
      intakeRecords.refetch();
      opsGroups.refetch();
      productsSold.refetch();
      mecOnboarded.refetch();
    },
    ...counts,
  };
}
